import { useEffect } from 'react';

/**
 * Applies the tenant's uploaded favicon to the document.
 *
 * `branding.faviconUrl` was saved by CPQ Branding, returned by `GET /tenantConfig`
 * and resolved to a servable URL by `useTenantConfig` (BRANDING_ASSET_FIELDS) -- but
 * nothing ever rendered it, so uploading a favicon in the console had no effect.
 *
 * Deliberately a no-op unless the tenant actually supplied a favicon:
 *  - no `faviconUrl` -> the document is left exactly as the host shell set it up, so
 *    the default (un-themed) experience is unchanged.
 *  - `enabled: false` -> used when sell_mfe is embedded as a web component, where the
 *    document belongs to the host app and an embedded MFE must not retitle or
 *    re-icon the page it is mounted into.
 *
 * The previous <link rel="icon"> href is restored on cleanup so a tenant switch (or
 * unmount of an embedded instance) cannot leave a stale icon behind.
 */
export const useTenantFavicon = (faviconUrl: string | undefined, enabled = true): void => {
  useEffect(() => {
    if (!enabled || !faviconUrl) return;

    const head = document.head;
    if (!head) return;

    let link = head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    const created = !link;
    const previousHref = link?.getAttribute('href') ?? null;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      head.appendChild(link);
    }
    link.setAttribute('href', faviconUrl);

    return () => {
      if (!link) return;
      if (created) {
        link.remove();
        return;
      }
      if (previousHref === null) link.removeAttribute('href');
      else link.setAttribute('href', previousHref);
    };
  }, [faviconUrl, enabled]);
};
